import { useFormContext } from "react-hook-form";
import { ReagentAlternativeProduct, ReagentSpecUpdate } from "@/types/specs";
import { useMemo, useCallback, useState, useEffect } from "react";
import ControlledRadioGroup from "@/components/Common/form/ControlledRadioGroup";
import { getPrice } from "../utils";

interface ChemicalSpecsAlternativeRadioGroupProps {
  spec_id: string;
  altItems: ReagentAlternativeProduct[];
}

export default function ChemicalSpecsAlternativeRadioGroup({
  spec_id,
  altItems,
}: ChemicalSpecsAlternativeRadioGroupProps) {
  const [selectedAltMapId, setSelectedAltMapId] = useState(
    altItems.find((item) => item.selected)?.alt_map_id || ""
  );

  const { watch, getValues, setValue, trigger } = useFormContext<{
    reagents: ReagentSpecUpdate[];
  }>();

  const radioOptions = useMemo(
    () =>
      altItems.map(({ alt_map_id, item }) => ({
        label: `${item.item_no} / ${item.item_name} / ${getPrice(item?.list_price)}`,
        value: alt_map_id,
      })),
    [altItems]
  );

  const reagentIndex = useMemo(() => {
    const reagents = getValues("reagents") || [];
    if (reagents.length === 0) return 0;

    const index = reagents.findIndex((reagent) => reagent?.spec_id === spec_id);

    return index === -1 ? reagents.length : index;
  }, [spec_id, getValues]);

  const watchedAltItems = watch(`reagents.${reagentIndex}.alt_items`);

  useEffect(() => {
    if (!watchedAltItems) return;

    setSelectedAltMapId(
      watchedAltItems?.find((item) => item.selected)?.alt_map_id || ""
    );
  }, [watchedAltItems]);

  const handleAlternativeChange = useCallback(
    (newSelectedId: string) => {
      setSelectedAltMapId(newSelectedId);

      setValue(`reagents.${reagentIndex}`, {
        spec_id,
        alt_items: altItems.map((item) => ({
          ...item,
          selected: item.alt_map_id === newSelectedId,
        })),
      });

      // 폼 상태를 더티로 만들기 위해 trigger 호출
      trigger(`reagents.${reagentIndex}`);
    },
    [spec_id, altItems, reagentIndex, setValue, trigger]
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-dark-red font-bold">써모피셔 대체품</p>
      <div className="flex flex-col gap-1 w-fit">
        <ControlledRadioGroup
          name={`reagents.${reagentIndex}`}
          options={radioOptions}
          onValueChange={handleAlternativeChange}
          value={selectedAltMapId}
          allowDeselect={true}
        />
      </div>
    </div>
  );
}
